import React, { useEffect, useMemo, useState } from "react";
import { Popper, PopperProps, TextField, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { City as CityLookup } from "country-state-city";
import { COUNTRIES, COUNTRY_BY_NAME } from "../pages/StationRequests/countryData";
import {
	normalizeLooseSearchString,
	shouldIncludeWorldLibraryPlaceName,
} from "../../utils/searchWorldLibraryCities";
import { StationRequestDestinationOption } from "../../services/FormService";
import {
	browseGeonamePlacesByCountry,
	searchGeonamePlacesForAdmin,
} from "../../services/geonamePlaceSearchService";

export function DestinationSuggestionPopper(props: PopperProps) {
	return (
		<Popper
			{...props}
			placement="bottom-start"
			modifiers={{ flip: { enabled: false } }}
			style={{ ...props.style, zIndex: 20000 }}
		/>
	);
}

export function mergeInputPropsNoBrowserAutofill(
	paramsInputProps: React.InputHTMLAttributes<HTMLInputElement>,
	uniqueName: string
): React.InputHTMLAttributes<HTMLInputElement> {
	return {
		...paramsInputProps,
		name: uniqueName,
		id: paramsInputProps.id || uniqueName,
		autoComplete: "off",
		autoCorrect: "off",
		autoCapitalize: "off",
		spellCheck: false,
		...( {
			"data-lpignore": "true",
			"data-1p-ignore": "true",
			"data-bwignore": "true",
			"data-form-type": "other",
		} as Record<string, string> ),
	} as React.InputHTMLAttributes<HTMLInputElement>;
}

export function filterOptionStrings(options: string[], inputValue: string, limit = 200): string[] {
	const raw = String(inputValue || "").trim();
	if (!raw) {
		return options.slice(0, Math.min(80, options.length));
	}
	const nq = normalizeLooseSearchString(raw);
	if (!nq) {
		return options.slice(0, Math.min(80, options.length));
	}
	const out = options.filter((o) => normalizeLooseSearchString(String(o)).includes(nq));
	return out.length
		? out.slice(0, limit)
		: options.filter((o) => o.toLowerCase().includes(raw.toLowerCase())).slice(0, limit);
}

export type WorldLibraryLocationPickerProps = {
	country: string;
	cityName: string;
	setCountry: (v: string) => void;
	setCityName: (v: string) => void;
	onClearSelection?: () => void;
	setDestinationId?: (id: string) => void;
	setSelectedLocation?: (loc: StationRequestDestinationOption | null) => void;
	mergedDestinations?: StationRequestDestinationOption[];
	/** catalogOnly islands: countries/cities derived from WhaleLO destinations only */
	catalogIsland?: boolean;
	uniqueInputPrefix?: string;
	caption?: React.ReactNode;
	/** Bật: danh sách thành phố từ GeoNamePlaces (API); tắt: country-state-city (cũ) */
	geonameBackend?: boolean;
};

/**
 * Country + city: mặc định dùng GeoNames (server DB); có thể tắt geonameBackend để dùng country-state-city.
 */
export default function WorldLibraryLocationPicker(props: WorldLibraryLocationPickerProps) {
	const {
		country,
		cityName,
		setCountry,
		setCityName,
		onClearSelection,
		setDestinationId,
		setSelectedLocation,
		mergedDestinations = [],
		catalogIsland = false,
		uniqueInputPrefix = "whalelo_world_lib",
		caption,
		geonameBackend = true,
	} = props;

	const mergedDestinationsSafe = Array.isArray(mergedDestinations) ? mergedDestinations : [];
	const [citySearchDebounced, setCitySearchDebounced] = useState("");
	const [geonameCityNames, setGeonameCityNames] = useState<string[]>([]);

	useEffect(() => {
		const t = window.setTimeout(() => setCitySearchDebounced(cityName), 400);
		return () => window.clearTimeout(t);
	}, [cityName]);

	useEffect(() => {
		if (!geonameBackend || catalogIsland) {
			return;
		}
		const countryTrim = String(country || "").trim();
		const countryInfo = COUNTRY_BY_NAME.get(countryTrim);
		if (!countryInfo) {
			setGeonameCityNames([]);
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const q = String(citySearchDebounced || "").trim();
				const rows =
					q.length >= 2
						? await searchGeonamePlacesForAdmin(q, { countryCode: countryInfo.code, limit: 300 })
						: await browseGeonamePlacesByCountry(countryInfo.code, 2000);
				if (cancelled) {
					return;
				}
				setGeonameCityNames(Array.from(new Set(rows.map((r) => r.cityName).filter(Boolean))));
			} catch {
				if (!cancelled) {
					setGeonameCityNames([]);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [geonameBackend, catalogIsland, country, citySearchDebounced]);

	const countryOptions = useMemo(() => {
		if (catalogIsland) {
			const destCountries = mergedDestinationsSafe
				.map((item) => String(item?.country || "").trim())
				.filter(Boolean);
			return Array.from(new Set(destCountries)).sort((a, b) => a.localeCompare(b));
		}
		const destCountries = mergedDestinationsSafe.map((item) => String(item?.country || "").trim()).filter(Boolean);
		const worldCountries = COUNTRIES.map((c) => c.name);
		const all = new Set([...destCountries, ...worldCountries]);
		return Array.from(all).sort((a, b) => a.localeCompare(b));
	}, [catalogIsland, mergedDestinationsSafe]);

	const cityOptions = useMemo(() => {
		if (!country) return [];
		if (catalogIsland) {
			const c = String(country).trim();
			const names = mergedDestinationsSafe
				.filter((item) => String(item.country || "").trim() === c)
				.map((d) => d.name);
			return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
		}
		const countryInfo = COUNTRY_BY_NAME.get(country);
		const destNames = new Set(
			mergedDestinationsSafe.filter((item) => item.country === country).map((d) => d.name)
		);
		if (geonameBackend) {
			const all = new Set([...Array.from(destNames), ...geonameCityNames]);
			return Array.from(all).sort((a, b) => a.localeCompare(b));
		}
		const worldCities = countryInfo
			? (CityLookup.getCitiesOfCountry(countryInfo.code) || [])
					.map((ci: { name: string }) => ci.name)
					.filter((name) => shouldIncludeWorldLibraryPlaceName(name, countryInfo.code))
			: [];
		const all = new Set([...Array.from(destNames), ...worldCities]);
		return Array.from(all).sort((a, b) => a.localeCompare(b));
	}, [catalogIsland, mergedDestinationsSafe, country, geonameBackend, geonameCityNames]);

	const countryTrim = String(country || "").trim();
	const cityDisabled = !countryTrim;

	const applyClear = () => {
		onClearSelection?.();
		setDestinationId?.("");
		setSelectedLocation?.(null);
	};

	return (
		<div>
			<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
				<Autocomplete
					freeSolo={true}
					options={countryOptions}
					inputValue={country}
					filterOptions={(options) => filterOptionStrings(options, country)}
					getOptionLabel={(option) => (typeof option === "string" ? option : String(option))}
					onChange={(_e: unknown, newValue: string | null) => {
						const next = newValue != null ? String(newValue) : "";
						setCountry(next);
						setCityName("");
						applyClear();
					}}
					onInputChange={(_e, newInputValue, reason) => {
						if (reason === "clear") {
							setCountry("");
							setCityName("");
							applyClear();
							return;
						}
						if (reason === "input" || reason === "reset") {
							setCountry(newInputValue);
							setCityName("");
							applyClear();
						}
					}}
					PopperComponent={DestinationSuggestionPopper}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="outlined"
							label="Country"
							placeholder="Search country (A–Z world list)"
							inputProps={mergeInputPropsNoBrowserAutofill(
								params.inputProps as React.InputHTMLAttributes<HTMLInputElement>,
								`${uniqueInputPrefix}_country`
							)}
						/>
					)}
				/>
				<Autocomplete
					freeSolo={true}
					options={cityOptions}
					inputValue={cityName}
					filterOptions={(options) => filterOptionStrings(options, cityName, 300)}
					onChange={(_e: unknown, value: string | null) => {
						const city = value != null ? String(value) : "";
						setCityName(city);
						if (!countryTrim) {
							applyClear();
							return;
						}
						const matchedDest =
							mergedDestinationsSafe.find((d) => d.name === city && d.country === countryTrim) || null;
						if (matchedDest) {
							setDestinationId?.(matchedDest.id);
							setSelectedLocation?.(matchedDest);
						} else {
							applyClear();
						}
					}}
					onInputChange={(_e, newValue, reason) => {
						if (reason === "clear") {
							setCityName("");
							applyClear();
							return;
						}
						const v = newValue ?? "";
						setCityName(v);
						if (!countryTrim) {
							applyClear();
							return;
						}
						const matchedDest =
							mergedDestinationsSafe.find((d) => d.name === v && d.country === countryTrim) || null;
						if (matchedDest) {
							setDestinationId?.(matchedDest.id);
							setSelectedLocation?.(matchedDest);
						} else {
							applyClear();
						}
					}}
					disabled={cityDisabled}
					PopperComponent={DestinationSuggestionPopper}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="outlined"
							label="City"
							placeholder={
								!countryTrim ? "Select country first" : "Search city (world library)"
							}
							inputProps={mergeInputPropsNoBrowserAutofill(
								params.inputProps as React.InputHTMLAttributes<HTMLInputElement>,
								`${uniqueInputPrefix}_city`
							)}
						/>
					)}
				/>
			</div>
			{caption ?? (
				<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
					{geonameBackend
						? "Chọn nước rồi thành phố (GeoNames trên server; cần đã import bảng địa điểm)."
						: "Pick country then city from the world library (country-state-city)."}
				</Typography>
			)}
		</div>
	);
}
